import React, { useEffect, useMemo, useState } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import { ServiceExecutiveService } from '../../services/serviceExecutiveService';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-emerald-100 text-emerald-700',
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  assigned: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

const ServiceRequestsList = ({
  user,
  status,
  onStatusChange,
  searchTerm,
  onSearchTermChange,
  onOpenDetail,
  onOpenRegistration,
  title = 'Service Requests',
  description = 'Manage and track service requests',
  hideStatusFilters = false,
}) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const activeStatus = status ?? '';
  const activeSearch = searchTerm ?? '';

  const loadRequests = async (page = 1, append = false, searchOverride) => {
    if (!user?.id) return;
    setLoading(true);
    const searchValue = (searchOverride ?? activeSearch).trim();
    const limit = 20;
    const response = searchValue
      ? await ServiceExecutiveService.searchServiceRequests(user.id, searchValue, activeStatus, page, limit)
      : await ServiceExecutiveService.getServiceRequests(user.id, activeStatus, page, limit);

    if (response.success) {
      setRequests((prev) => (append ? [...prev, ...response.data] : response.data));
      setHasMore(response.data.length === limit);
      setCurrentPage(page);
    } else {
      setRequests(append ? requests : []);
      setHasMore(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user?.id) return;
    if (activeSearch.trim().length === 0) {
      loadRequests(1, false, '');
    } else {
      loadRequests(1, false, activeSearch);
    }
  }, [user?.id, activeStatus]);

  const stats = useMemo(() => ({
    total: requests.length,
    pending: requests.filter((r) => r.request_status === 'pending').length,
    completed: requests.filter((r) => r.request_status === 'completed').length,
  }), [requests]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600">{description}</p>
        </div>
        {onOpenRegistration && (
          <button
            type="button"
            onClick={onOpenRegistration}
            className="px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600"
          >
            + New Request
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="flex-1 flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2">
            <Search size={18} className="text-gray-400" />
            <input
              value={activeSearch}
              onChange={(event) => onSearchTermChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  loadRequests(1, false, event.currentTarget.value);
                }
              }}
              placeholder="Search service requests..."
              className="flex-1 outline-none text-sm text-gray-700"
            />
            {activeSearch.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  onSearchTermChange('');
                  loadRequests(1, false, '');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => loadRequests(1, false)}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Search
          </button>
        </div>

        {!hideStatusFilters && (
          <div className="mt-4 flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value || 'all'}
                type="button"
                onClick={() => onStatusChange(option.value)}
                className={`px-3 py-1 rounded-full border text-sm ${
                  activeStatus === option.value
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Pending</p>
          <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-10 shadow-sm border border-gray-200 text-center">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full spinner mx-auto mb-3"></div>
          <p className="text-gray-600">Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl p-10 shadow-sm border border-gray-200 text-center">
          <AlertCircle size={40} className="text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No service requests found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{request.customer_name || 'Customer'}</h3>
                  <p className="text-sm text-gray-500">{request.vehicle_number || 'Vehicle'} · {request.service_type || 'Service'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {request.priority && (
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${PRIORITY_COLORS[request.priority] || 'bg-gray-100 text-gray-600'}`}>
                      {request.priority.toUpperCase()}
                    </span>
                  )}
                  {request.request_status && (
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_COLORS[request.request_status] || 'bg-gray-100 text-gray-600'}`}>
                      {request.request_status.replace('_', ' ').toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-gray-700 text-sm mb-3">{request.problem || 'No description provided.'}</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <span>Requested: {formatDateTime(request.requested_at || request.created_at)}</span>
                {request.location_link && <span>Location: {request.location_link}</span>}
              </div>
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => onOpenDetail(request)}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && !loading && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => loadRequests(currentPage + 1, true)}
            className="px-6 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestsList;
